import { ReactNode } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { ShieldAlert } from "lucide-react";

interface InstructorFeatureGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  showTooltip?: boolean;
}

export default function InstructorFeatureGuard({ 
  children, 
  fallback,
  showTooltip = true 
}: InstructorFeatureGuardProps) {
  const { user, emailVerified } = useAuthContext();

  // Only apply guard to instructors
  if (user?.role !== "instructor") {
    return <>{children}</>;
  }

  // If instructor and email is verified, allow access
  if (emailVerified) {
    return <>{children}</>;
  }

  // If instructor and email is NOT verified, show fallback or disabled state
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default fallback: show tooltip message
  return (
    <div className="relative group">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" />
          ต้องยืนยันอีเมลก่อนใช้งานฟีเจอร์นี้
        </div>
      )}
    </div>
  );
}

// Hook for checking if feature is accessible
export function useInstructorFeatureAccess() {
  const { user, emailVerified } = useAuthContext();

  // Students always have access
  if (user?.role !== "instructor") {
    return { hasAccess: true, reason: null };
  }

  // Instructors need email verification
  if (!emailVerified) {
    return { 
      hasAccess: false, 
      reason: "ต้องยืนยันอีเมลก่อนใช้งานฟีเจอร์นี้" 
    };
  }

  return { hasAccess: true, reason: null };
}
